<template>
  <div ref="root">
    <slot></slot>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, ref } from 'vue';

const root = ref<HTMLElement | null>(null);
let hideTimeout: number;
let isMobile: boolean;
let trigger: HTMLElement | null;
let detail: HTMLElement | null;

const showDetail = () => {
  if (!detail) return;
  clearTimeout(hideTimeout);
  detail.classList.add('active');
  trigger?.setAttribute('aria-expanded', 'true');
};

const hideDetail = () => {
  if (!detail) return;
  hideTimeout = window.setTimeout(() => {
    detail?.classList.remove('active');
    trigger?.setAttribute('aria-expanded', 'false');
  }, 50);
};

const toggleDetail = () => {
  if (!detail) return;
  const isVisible = detail.classList.contains('active');
  if (isVisible) {
    hideDetail();
  } else {
    showDetail();
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (
    detail &&
    detail.classList.contains('active') &&
    root.value &&
    !root.value.contains(event.target as Node)
  ) {
    hideDetail();
  }
};

const setupDesktopListeners = () => {
  if (trigger && detail) {
    trigger.addEventListener('mouseenter', showDetail);
    trigger.addEventListener('mouseleave', hideDetail);
    // Keyboard/tap activation toggles it (the trigger is a real <button>).
    trigger.addEventListener('click', toggleDetail);
    detail.addEventListener('mouseenter', showDetail);
    detail.addEventListener('mouseleave', hideDetail);
  }
};

const removeDesktopListeners = () => {
  if (trigger && detail) {
    trigger.removeEventListener('mouseenter', showDetail);
    trigger.removeEventListener('mouseleave', hideDetail);
    trigger.removeEventListener('click', toggleDetail);
    detail.removeEventListener('mouseenter', showDetail);
    detail.removeEventListener('mouseleave', hideDetail);
  }
};

const setupMobileListeners = () => {
  if (trigger) {
    trigger.addEventListener('click', toggleDetail);
    document.addEventListener('click', handleClickOutside);
  }
};

const removeMobileListeners = () => {
  if (trigger) {
    trigger.removeEventListener('click', toggleDetail);
    document.removeEventListener('click', handleClickOutside);
  }
};

const handleResize = () => {
  const wasMobile = isMobile;
  isMobile = window.innerWidth < 768;

  if (wasMobile === isMobile) return;

  if (isMobile) {
    removeDesktopListeners();
    setupMobileListeners();
  } else {
    removeMobileListeners();
    setupDesktopListeners();
  }
};

onMounted(() => {
  if (root.value) {
    trigger = root.value.querySelector('[data-detail-trigger]');
    detail = root.value.querySelector('[data-detail-content]');
    isMobile = window.innerWidth < 768;
    if (isMobile) {
      setupMobileListeners();
    } else {
      setupDesktopListeners();
    }
    window.addEventListener('resize', handleResize);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (isMobile) {
    removeMobileListeners();
  } else {
    removeDesktopListeners();
  }
});
</script>
